<template>
  <div class="module-progress-component">
    <div class="progress-header">
      <div class="progress-info">
        <span class="progress-label">
          <i class="fa-solid fa-book-open"></i>
          {{ currentLessonIndex + 1 }} de {{ totalLessons }}
        </span>
        <span class="progress-percentage">{{ progressPercentage }}%</span>
      </div>
      <div class="progress-bar-container">
        <div
          class="progress-bar-fill"
          :style="{ width: `${progressPercentage}%` }"
        >
          <div class="progress-glow"></div>
        </div>
      </div>
    </div>

    <div class="mini-roadmap" v-if="showRoadmap">
      <div
        v-for="(item, index) in visibleItems"
        :key="item.id"
        class="roadmap-item"
        :class="{
          'completed': isCompleted(item),
          'current': isCurrent(item),
          'locked': isLocked(item)
        }"
        :title="getTitle(item)"
      >
        <div class="roadmap-dot">
          <i v-if="isCompleted(item)" class="fa-solid fa-check"></i>
          <i v-else-if="isCurrent(item)" class="fa-solid fa-play"></i>
          <i v-else-if="isLocked(item)" class="fa-solid fa-lock"></i>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div v-if="index < visibleItems.length - 1" class="roadmap-connector"></div>
      </div>
    </div>
  </div>
</template>

<script>
import get from "lodash/get";

export default {
  name: "ModuleProgress",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    currentItem: {
      type: Object,
      default: () => ({})
    },
    showRoadmap: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    totalLessons() {
      return this.items.length;
    },
    completedLessons() {
      return this.items.filter(item => get(item, "progress.finished_at", false)).length;
    },
    currentLessonIndex() {
      const index = this.items.findIndex(item => item.id === this.currentItem.id);
      return index >= 0 ? index : 0;
    },
    progressPercentage() {
      if (this.totalLessons === 0) return 0;
      return Math.round((this.completedLessons / this.totalLessons) * 100);
    },
    visibleItems() {
      // Mostrar no máximo 7 itens para não sobrecarregar a UI
      if (this.items.length <= 7) return this.items;

      // Centralizar no item atual
      const currentIndex = this.currentLessonIndex;
      const start = Math.max(0, currentIndex - 3);
      const end = Math.min(this.items.length, start + 7);

      return this.items.slice(start, end);
    }
  },
  methods: {
    isCompleted(item) {
      return get(item, "progress.finished_at", false);
    },
    isCurrent(item) {
      return item.id === this.currentItem.id;
    },
    isLocked(item) {
      return get(item, "is_blocked.status", false);
    },
    getTitle(item) {
      return item.name || item.title || "";
    }
  }
};
</script>

<style lang="scss" scoped>
.module-progress-component {
  margin-bottom: 16px;
  padding: 16px 20px;
  background: rgba(62, 86, 172, 0.08);
  border-radius: 12px;
  border: 1px solid rgba(62, 86, 172, 0.15);
  backdrop-filter: blur(10px);

  .progress-header {
    .progress-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    .progress-label {
      font-size: 13px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.85);
      display: flex;
      align-items: center;
      gap: 8px;

      i {
        color: #3e56ac;
        font-size: 14px;
      }
    }

    .progress-percentage {
      font-size: 14px;
      font-weight: 700;
      color: #ab85e5;
    }

    .progress-bar-container {
      height: 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      overflow: hidden;
      position: relative;
    }

    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #3e56ac 0%, #ab85e5 100%);
      border-radius: 10px;
      transition: width 0.5s ease-out;
      position: relative;

      .progress-glow {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        background: radial-gradient(circle, rgba(171, 133, 229, 0.6) 0%, transparent 70%);
        border-radius: 50%;
      }
    }
  }

  .mini-roadmap {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    gap: 4px;

    .roadmap-item {
      display: flex;
      align-items: center;

      .roadmap-dot {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
        background: rgba(255, 255, 255, 0.1);
        border: 2px solid rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.5);
        transition: all 0.3s ease;

        i {
          font-size: 10px;
        }
      }

      .roadmap-connector {
        width: 12px;
        height: 2px;
        background: rgba(255, 255, 255, 0.15);
        margin: 0 2px;
      }

      &.completed {
        .roadmap-dot {
          background: rgba(98, 173, 62, 0.2);
          border-color: #62ad3e;
          color: #62ad3e;
        }
        .roadmap-connector {
          background: rgba(98, 173, 62, 0.5);
        }
      }

      &.current {
        .roadmap-dot {
          background: linear-gradient(135deg, #3e56ac 0%, #ab85e5 100%);
          border-color: #ab85e5;
          color: white;
          box-shadow: 0 0 12px rgba(171, 133, 229, 0.5);
          animation: pulse 2s ease-in-out infinite;
        }
      }

      &.locked {
        .roadmap-dot {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 12px rgba(171, 133, 229, 0.5);
    }
    50% {
      box-shadow: 0 0 20px rgba(171, 133, 229, 0.8);
    }
  }
}

// Responsivo
@media screen and (max-width: 576px) {
  .module-progress-component {
    padding: 12px 16px;

    .mini-roadmap {
      .roadmap-dot {
        width: 24px;
        height: 24px;
        font-size: 10px;
      }
      .roadmap-connector {
        width: 8px;
      }
    }
  }
}
</style>

